import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

interface SermonMediaFile {
  id: string;
  sermon_id: string;
  section_id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_path: string;
  created_at: string;
  updated_at: string;
}

interface UploadOptions {
  sermonId: string;
  sectionId: string;
  file: File;
  onProgress?: (progress: number) => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

async function retryOperation<T>(
  operation: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY
): Promise<T> {
  try {
    return await operation();
  } catch (error) {
    // Check if it's a network-related error
    const isNetworkError = error instanceof Error && (
      error.message.includes('Failed to fetch') ||
      error.message.includes('NetworkError') ||
      error.message.includes('network') ||
      error.message.includes('connection') ||
      error.message.includes('timeout') ||
      error.name === 'TypeError'
    );

    if (retries > 0 && isNetworkError) {
      console.log(`Network error detected, retrying in ${delay}ms... (${retries} attempts left)`);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryOperation(operation, retries - 1, delay * 2);
    }
    throw error;
  }
}

export function useSermonMedia(sermonId?: string) {
  const [media, setMedia] = useState<SermonMediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load media for a specific sermon
  const loadMedia = useCallback(async (id?: string) => {
    const targetSermonId = id || sermonId;
    if (!targetSermonId) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await retryOperation(async () => 
        supabase
          .from('sermon_media')
          .select('*')
          .eq('sermon_id', targetSermonId)
          .order('created_at', { ascending: false })
      );

      if (fetchError) throw fetchError;
      setMedia(data || []);
    } catch (err) {
      console.error('Error loading sermon media:', err);
      setError(err instanceof Error ? err.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [sermonId]);

  // Load media for a specific section
  const loadSectionMedia = useCallback(async (id?: string, sectionId?: string) => {
    const targetSermonId = id || sermonId;
    if (!targetSermonId || !sectionId) return [];

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await retryOperation(async () =>
        supabase
          .from('sermon_media')
          .select('*')
          .eq('sermon_id', targetSermonId)
          .eq('section_id', sectionId)
          .order('created_at', { ascending: false })
      );

      if (fetchError) throw fetchError;
      return data || [];
    } catch (err) {
      console.error('Error loading section media:', err);
      setError(err instanceof Error ? err.message : 'Failed to load section media');
      return [];
    } finally {
      setLoading(false);
    }
  }, [sermonId]);

  // Upload a file
  const uploadFile = useCallback(async ({ sermonId, sectionId, file, onProgress }: UploadOptions) => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await retryOperation(async () =>
        supabase.auth.getUser()
      );

      if (!user) throw new Error('Not authenticated');

      // Create a unique file path
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${user.id}/${sermonId}/${sectionId}/${fileName}`;

      // Upload to storage
      const { error: uploadError, data: uploadData } = await retryOperation(async () =>
        supabase.storage
          .from('sermon-media')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false,
            onUploadProgress: (progress) => {
              if (onProgress) {
                const percent = (progress.loaded / progress.total) * 100;
                onProgress(percent);
              }
            }
          })
      );

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: { publicUrl } } = supabase.storage
        .from('sermon-media')
        .getPublicUrl(filePath);

      // Save record to database
      const { data: mediaData, error: mediaError } = await retryOperation(async () =>
        supabase
          .from('sermon_media')
          .insert([{
            user_id: user.id,
            sermon_id: sermonId,
            section_id: sectionId,
            file_name: file.name,
            file_type: file.type,
            file_size: file.size,
            file_path: filePath
          }])
          .select()
          .single()
      );

      if (mediaError) throw mediaError;

      // Refresh media list
      await loadMedia(sermonId);

      return { ...mediaData, publicUrl };
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadMedia]);

  // Delete a file
  const deleteFile = useCallback(async (mediaId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Get the file path first
      const { data: mediaData, error: fetchError } = await retryOperation(async () =>
        supabase
          .from('sermon_media')
          .select('file_path, sermon_id')
          .eq('id', mediaId)
          .single()
      );

      if (fetchError) throw fetchError;
      if (!mediaData) throw new Error('Media not found');

      // Delete from storage
      const { error: storageError } = await retryOperation(async () =>
        supabase.storage
          .from('sermon-media')
          .remove([mediaData.file_path])
      );

      if (storageError) throw storageError;

      // Delete from database
      const { error: deleteError } = await retryOperation(async () =>
        supabase
          .from('sermon_media')
          .delete()
          .eq('id', mediaId)
      );

      if (deleteError) throw deleteError;

      // Refresh media list
      await loadMedia(mediaData.sermon_id);

      return true;
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete file');
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadMedia]);

  // Get signed URL for a file path
  const getSignedUrl = useCallback(async (filePath: string) => {
    try {
      const { data, error } = await retryOperation(async () =>
        supabase.storage
          .from('sermon-media')
          .createSignedUrl(filePath, 3600) // 1 hour expiry
      );

      if (error) throw error;
      return data.signedUrl;
    } catch (err) {
      console.error('Error getting signed URL:', err);
      return null;
    }
  }, []);

  // Get public URL for a file path
  const getPublicUrl = useCallback((filePath: string) => {
    const { data: { publicUrl } } = supabase.storage
      .from('sermon-media')
      .getPublicUrl(filePath);
    
    return publicUrl;
  }, []);

  // Download a file
  const downloadFile = useCallback(async (filePath: string, fileName: string) => {
    try {
      setLoading(true);
      setError(null);

      // Get a signed URL for the file with retry
      const { data, error } = await retryOperation(async () =>
        supabase.storage
          .from('sermon-media')
          .createSignedUrl(filePath, 300) // URL valid for 5 minutes (longer for better reliability)
      );

      if (error) throw error;
      
      // Use the signed URL to download the file with retry and timeout
      const response = await retryOperation(async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
        
        try {
          const response = await fetch(data.signedUrl, {
            signal: controller.signal,
            headers: {
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            }
          });
          clearTimeout(timeoutId);
          return response;
        } catch (error) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            throw new Error('Download timed out. Please check your internet connection and try again.');
          }
          throw error;
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      return true;
    } catch (err) {
      console.error('Error downloading file:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to download file';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Download all images for a sermon as a zip file
  const downloadAllImages = useCallback(async (sermonId: string, sermonTitle: string) => {
    try {
      setLoading(true);
      setError(null);

      // Get all media files for the sermon
      const { data: allMedia, error: fetchError } = await retryOperation(async () =>
        supabase
          .from('sermon_media')
          .select('*')
          .eq('sermon_id', sermonId)
          .order('created_at', { ascending: false })
      );

      if (fetchError) throw fetchError;
      if (!allMedia || allMedia.length === 0) {
        throw new Error('No media files found for this sermon');
      }

      // Filter for image files only
      const imageFiles = allMedia.filter(file => 
        file.file_type.startsWith('image/')
      );

      if (imageFiles.length === 0) {
        throw new Error('No image files found for this sermon');
      }

      // Dynamic import of JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      
      let downloadedCount = 0;
      const totalCount = imageFiles.length;

      // Download each image file and add to zip
      for (const file of imageFiles) {
        try {
          // Get signed URL for the file
          const { data, error } = await retryOperation(async () =>
            supabase.storage
              .from('sermon-media')
              .createSignedUrl(file.file_path, 300)
          );

          if (error) {
            console.warn(`Failed to get signed URL for ${file.file_name}:`, error);
            continue;
          }

          // Fetch the file
          const response = await retryOperation(async () => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);
            
            try {
              const response = await fetch(data.signedUrl, {
                signal: controller.signal,
                headers: {
                  'Cache-Control': 'no-cache',
                  'Pragma': 'no-cache'
                }
              });
              clearTimeout(timeoutId);
              return response;
            } catch (error) {
              clearTimeout(timeoutId);
              throw error;
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            zip.file(file.file_name, blob);
            downloadedCount++;
          } else {
            console.warn(`Failed to download ${file.file_name}: ${response.status}`);
          }
        } catch (error) {
          console.warn(`Error downloading ${file.file_name}:`, error);
        }
      }

      if (downloadedCount === 0) {
        throw new Error('Failed to download any image files');
      }

      // Generate and download the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sermonTitle.toLowerCase().replace(/\s+/g, '-')}-images.zip`;
      document.body.appendChild(a);
      a.click();
      
      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      return {
        success: true,
        downloadedCount,
        totalCount
      };
    } catch (err) {
      console.error('Error downloading all images:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to download images';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load media on mount if sermonId is provided
  useEffect(() => {
    if (sermonId) {
      loadMedia();
    }
  }, [sermonId, loadMedia]);

  return {
    media,
    loading,
    error,
    loadMedia,
    loadSectionMedia,
    uploadFile,
    deleteFile,
    getPublicUrl,
    getSignedUrl,
    downloadFile,
    downloadAllImages
  };
}